import React from 'react';
import { useModal } from 'react-modal-hook';
import { useDispatch, useSelector } from 'react-redux';
import { Button, H3, ModalFactory, Switch, Text } from 'components';
import { appConfig } from 'config/app';
import { EnableTextNotifications } from 'modals';
import { selectIsLoadingAPIRequest } from 'store/ApiClient';
import { IStore } from 'store/types';
import { selectCurrentUser, updatePaymentNotificationMethodRequest, User } from 'store/User';
import { IdentityType, PaymentNotificationMethod } from 'types';
import { hasVerifiedIdentityType } from 'utils/identity';
import { NotificationContentLayout, NotificationDisclaimer, NotificationSection, NotificationSectionHeader, NotificationTextLayout } from './styles';

interface INotificationsFormProps {
  alignHeadersCenter?: boolean;
  missingMethodURL: string;
}

const NotificationsForm = ({ alignHeadersCenter, missingMethodURL }: INotificationsFormProps) => {
  // UI State
  const dispatch = useDispatch();
  const { settings, identities } = useSelector(selectCurrentUser());
  const emailEnabled = settings?.paymentNotificationMethod === PaymentNotificationMethod.EMAIL || settings?.paymentNotificationMethod === PaymentNotificationMethod.BOTH;
  const textEnabled = settings?.paymentNotificationMethod === PaymentNotificationMethod.TEXT || settings?.paymentNotificationMethod === PaymentNotificationMethod.BOTH;

  // Modal
  const [showModal, hideModal] = useModal(() => {
    const confirmEnableText = () => {
      const paymentMethod = emailEnabled ? PaymentNotificationMethod.BOTH : PaymentNotificationMethod.TEXT;

      dispatch(updatePaymentNotificationMethodRequest(paymentMethod));
      hideModal();
    };
    return (
      <ModalFactory customModal isActive onClose={hideModal}>
        <EnableTextNotifications onCancel={hideModal} onClose={hideModal} onConfirm={confirmEnableText} data-cy="text-notifications-modal" />
      </ModalFactory>
    );
  }, [emailEnabled, updatePaymentNotificationMethodRequest, dispatch]);

  // Email Notifications
  const isLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, User.UPDATE_PAYMENT_NOTIFICATION_METHOD_REQUEST));

  const enableEmail = () => {
    const paymentMethod = textEnabled ? PaymentNotificationMethod.BOTH : PaymentNotificationMethod.EMAIL;
    dispatch(updatePaymentNotificationMethodRequest(paymentMethod));
  };

  const disableEmail = () => {
    const paymentMethod = textEnabled ? PaymentNotificationMethod.TEXT : PaymentNotificationMethod.NONE;
    dispatch(updatePaymentNotificationMethodRequest(paymentMethod));
  };

  const handleEmailToggle = () => (emailEnabled ? disableEmail() : enableEmail());

  // Text Notifications
  const disableText = () => {
    const paymentMethod = emailEnabled ? PaymentNotificationMethod.EMAIL : PaymentNotificationMethod.NONE;
    dispatch(updatePaymentNotificationMethodRequest(paymentMethod));
  };

  const enableText = () => {
    showModal();
  };

  const toggleText = () => {
    if (textEnabled) {
      disableText();
    } else {
      enableText();
    }
  };

  const currentUserHasEmail = hasVerifiedIdentityType(identities, IdentityType.EMAIL);
  const currentUserHasMobile = hasVerifiedIdentityType(identities, IdentityType.MOBILE);

  const ENABLE_TEXT_NUMBER = process.env.REACT_APP_NOTIFICATIONS_ENABLE_PHONE_NUMBER;

  return (
    <>
      <NotificationSection data-cy="notification-email">
        <NotificationSectionHeader alignCenter={alignHeadersCenter}>
          <H3>Email Notifications</H3>
        </NotificationSectionHeader>
        <NotificationContentLayout>
          {currentUserHasEmail ? (
            <>
              <Text size="small">Enable email notifications to receive payment alerts.</Text>
              <Switch disabled={isLoading} checked={emailEnabled} onChange={handleEmailToggle} data-cy="email-notification-switch" />
            </>
          ) : (
            <>
              <Text size="small">Add an email address to your account to enable email notifications to receive payment alerts.</Text>
              <Button appearance="primary" size="small" label="Add" to={missingMethodURL} data-cy="add-email" />
            </>
          )}
        </NotificationContentLayout>
      </NotificationSection>

      <NotificationSection data-cy="notification-text">
        <NotificationSectionHeader alignCenter={alignHeadersCenter}>
          <H3>Text Notifications</H3>
        </NotificationSectionHeader>

        <NotificationContentLayout>
          {currentUserHasMobile ? (
            <>
              <NotificationTextLayout>
                <Text size="small">
                  {'Enable '}
                  <a href={appConfig.LINKS.TERMS_OF_USE} target="_blank" rel="noopener noreferrer" data-cy="terms-of-use-text-link">
                    text notifications
                  </a>
                  {' to receive payment alerts.'}
                </Text>
              </NotificationTextLayout>
              <Switch disabled={isLoading} checked={textEnabled} onChange={toggleText} data-cy="text-notification-switch" />
            </>
          ) : (
            <>
              <NotificationTextLayout>
                <Text size="small">
                  {'Add a mobile number to your account to enable '}
                  <a href={appConfig.LINKS.TERMS_OF_USE} target="_blank" rel="noopener noreferrer" data-cy="terms-of-use-text-link">
                    text notifications
                  </a>
                  {' to receive payment alerts.'}
                </Text>
              </NotificationTextLayout>

              <Button appearance="primary" size="small" label="Add" to={missingMethodURL} data-cy="add-phone-number" />
            </>
          )}
        </NotificationContentLayout>

        {currentUserHasMobile && textEnabled && <NotificationDisclaimer>{`Not receiving your notifications? Send "START" to ${ENABLE_TEXT_NUMBER}.`}</NotificationDisclaimer>}
      </NotificationSection>
    </>
  );
};

export default NotificationsForm;
